import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import QRCode from 'qrcode';

import { useTranslation } from 'react-i18next';
import ReactCodeInput from 'react-verification-code-input';
import Button from '../../../../components/Button';
import * as actions from '../../actions';
import { selectors } from '../../reducer';
import { selectors as selectorsLogin } from '../../../LoginPage/reducer';

import * as Styled from './ApplyBlock.styles';
import { distributeClientBonus } from '../../helpers';

interface ApplyBlockProps {
  setShowWithBonuses: (a: boolean) => void;
}

const ApplyBlock: React.FC<ApplyBlockProps> = ({ setShowWithBonuses }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [value, setValue] = useState<string>('');
  const [qrSrc, setQrSrc] = useState<string>('');
  const [qrError, setQrError] = useState<string>('');
  const dataUser = useSelector(selectorsLogin.userData);
  const clientInfo = useSelector(selectors.clientInfo);
  const chosenReceipt = useSelector(selectors.chosenReceipt);
  const appliedBonuses = useSelector(selectors.appliedBonuses);
  const appliedBonusesLoading = useSelector(selectors.appliedBonusesLoading);
  const appliedBonusesError = useSelector(selectors.appliedBonusesError);
  const submitOrderError = useSelector(selectors.submitOrderError);
  const submitOrderResult = useSelector(selectors.submitOrderResult);
  const submitOrderLoading = useSelector(selectors.submitOrderLoading);

  const handleCodeInputChange = (newValue: string) => {
    setValue(newValue);
  };

  const handleApplyBonuses = () => {
    if (!clientInfo?.phone) return;
    setValue('');
    dispatch(actions.applyBonuses.request(clientInfo.phone));
  };

  const handleApply = () => {
    if (!clientInfo?.phone) return;
    const smsCode = value?.length === 4 ? { sms_code: value } : {};
    const currentItems = distributeClientBonus(
      chosenReceipt?.items ?? [],
      value?.length === 4 ? clientInfo?.balance ?? 0 : 0
    );

    const totalBonuses = (bonuses: 'bonusesToClient' | 'bonusesToPharmacist') =>
      currentItems?.reduce((acc, item) => acc + item[bonuses], 0) || 0;

    dispatch(
      actions.submitLoyaltyOrder.request({
        ...smsCode,
        phone: clientInfo.phone,
        bonus_used: value?.length === 4,
        doc_id: chosenReceipt?.doc_id,
        source_code: dataUser?.sourceCode,
        items: currentItems.map((item) => ({
          ware_id: item.ware_id,
          price: item.base_price - item.appliedBonus / item.quantity,
          quantity: item.quantity,
          name: item.name,
        })),
        client_bonus_debit: currentItems?.reduce(
          (acc, item) => acc + item.appliedBonus,
          0
        ),
        client_bonus_credit: totalBonuses('bonusesToClient'),
        pharmacist_bonus_credit: totalBonuses('bonusesToPharmacist'),
        postEffect: async () => {
          if (value?.length !== 4) {
            setQrSrc('');
            return;
          }
          try {
            setQrError('');
            const payload = JSON.stringify({
              doc_id: chosenReceipt?.doc_id,
              items: currentItems?.map((item) => ({
                ware_id: item.ware_id,
                price: item.base_price - item.appliedBonus / item.quantity,
                quantity: item.quantity,
              })),
            });
            const dataUrl = await QRCode.toDataURL(payload, {
              errorCorrectionLevel: 'M',
              margin: 1,
              scale: 4,
            });
            setQrSrc(dataUrl);
          } catch (e: any) {
            console.error('QR generation error:', e);
            setQrSrc('');
            setQrError(e?.message || 'Не удалось сгенерировать QR');
          }
        },
      })
    );
  };

  useEffect(() => {
    if (!chosenReceipt) {
      setQrError('');
      setQrSrc('');
      setValue('');
      setShowWithBonuses(true);
      dispatch(actions.resetApplyBonuses());
      dispatch(actions.resetSubmitOrder());
    }
  }, [chosenReceipt]);

  if (!clientInfo || !clientInfo?.phone) return null;

  return (
    <Styled.ApplyBlock>
      <Styled.ApplyLeftBlock>
        <Button
          onClick={handleApplyBonuses}
          text={t('LoyaltyPage.APPLY_BONUSES')}
          variant="contained"
          disabled={appliedBonusesLoading}
        />

        {appliedBonusesError && (
          <div className="error">{appliedBonusesError}</div>
        )}
        {appliedBonuses && (
          <ReactCodeInput
            fields={4}
            fieldWidth={40}
            fieldHeight={40}
            placeholder={[`×`, '×', '×', '×']}
            values={[...value]}
            onChange={handleCodeInputChange}
          />
        )}
      </Styled.ApplyLeftBlock>
      <Button
        onClick={handleApply}
        text={t('LoyaltyPage.APPLY')}
        variant="contained"
        disabled={!!submitOrderLoading || !!submitOrderResult}
      />
      {submitOrderError && <div className="error">{submitOrderError}</div>}
      {/* QR */}
      <div
        style={{
          minHeight: 80,
          display: 'flex',
          alignItems: 'flex-end',
          alignSelf: 'flex-start',
          justifyContent: 'center',
        }}
      >
        {qrSrc && submitOrderResult && (
          <img alt="QR" width={120} height={120} src={qrSrc} />
        )}
      </div>
      {qrError && <div className="error">{qrError}</div>}
    </Styled.ApplyBlock>
  );
};

export default ApplyBlock;
